import {
    Autocomplete, Button, Chip, FormControl, FormHelperText, IconButton,
    InputLabel, MenuItem, Select, Stack, TextField,
    Delete, Edit
} from '@leap4/frontend';
import React, { useState } from 'react';

interface Filter {
    id: string;
    dimension: string;
    action: string;
    specTarget: { key: string; value: string }[];
}

interface FilterProps {
    dimensionsOptions: { key: string; value: string }[];
    filtersData: Filter[];
    onChange: (id: string, field: string, value: any) => void;
    onAddFilter: () => void;
    onDeleteFilter: (id: string) => void;
}

const actionOptions = [
    { key: 'include', value: 'Include' },
    { key: 'exclude', value: 'Exclude' },
];

interface FilterItemProps {
    filter: Filter;
    dimensionsOptions: { key: string; value: string }[];
    onChange: (id: string, field: string, value: any) => void;
    onDeleteFilter: (id: string) => void;
    isFirst: boolean;
    onAddFilter: () => void;
    isLast: boolean;
}

const FilterItem: React.FC<FilterItemProps> = ({
    filter,
    dimensionsOptions,
    onChange,
    onDeleteFilter,
    onAddFilter,
    isLast,
}) => {

    const [inputValue, setInputValue] = useState('');

    const setValues = (values: string[]) => {
        onChange(filter.id, 'specTarget', values.map(v => ({ key: v, value: v })));
    };

    const commitPendingInput = () => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        setInputValue('');
        if (filter.specTarget.some(t => t.value === trimmed)) return;
        setValues([...filter.specTarget.map(t => t.value), trimmed]);
    };

    const editChip = (value: string) => {
        setValues(filter.specTarget.filter(t => t.value !== value).map(t => t.value));
        setInputValue(value);
    };

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            marginBottom={1}
        >
            <FormControl variant="outlined" size="small" style={{ width: '15%' }}>
                <InputLabel>Dimension</InputLabel>
                <Select
                    value={filter.dimension}
                    onChange={(e) => onChange(filter.id, 'dimension', e.target.value)}
                    label="Dimension"
                >
                    {dimensionsOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                            {option.value}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl variant="outlined" size="small" style={{ width: '10%' }}>
                <InputLabel>Action</InputLabel>
                <Select
                    value={filter.action}
                    onChange={(e) => onChange(filter.id, 'action', e.target.value)}
                    label="Action"
                >
                    {actionOptions.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                            {option.value}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Autocomplete
                multiple
                freeSolo
                clearOnBlur={false}
                size="small"
                style={{ width: '40%' }}
                options={[]}
                value={filter.specTarget.map(t => t.value)}
                inputValue={inputValue}
                onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
                onChange={(_event, newValue) => setValues(newValue as string[])}
                onBlur={commitPendingInput}
                renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                            <Chip
                                key={key}
                                size="small"
                                label={option}
                                icon={
                                    <Edit
                                        fontSize="small"
                                        style={{ cursor: 'pointer' }}
                                        aria-label={`edit ${option}`}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={(e) => { e.stopPropagation(); editChip(option); }}
                                    />
                                }
                                {...tagProps}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Values (type, then Enter or click away to add)"
                        placeholder="Add values..."
                    />
                )}
            />

            <IconButton
                color="error"
                onClick={() => onDeleteFilter(filter.id)}
                aria-label="delete"
            >
                <Delete />
            </IconButton>

            {isLast && (
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onAddFilter}
                    style={{ marginLeft: '8px' }}
                >
                    + Add Filter
                </Button>
            )}
        </Stack>
    );
};

const Filters: React.FC<FilterProps> = ({
    dimensionsOptions,
    filtersData,
    onChange,
    onAddFilter,
    onDeleteFilter,
}) => {
    return (
        <div>
            <FormHelperText>
                Filters
            </FormHelperText>
            {filtersData.map((filter, index) => (
                <FilterItem
                    key={filter.id}
                    filter={filter}
                    dimensionsOptions={dimensionsOptions}
                    onChange={onChange}
                    onDeleteFilter={onDeleteFilter}
                    isFirst={index === 0}
                    onAddFilter={onAddFilter}
                    isLast={index === filtersData.length - 1}
                />
            ))}
        </div>
    );
};

export default Filters;
